import React from "react";
import MultipleChoiceOption from "./MultipleChoiceOption";
import {MCColors, MCDisabledColor} from "../common/questionConstants";
import {QuestionDisplayProps} from "../Question";
import {createUseStyles, useTheme} from "react-jss";
import {Theme} from "engage-client/theme";
import {AggregateResponses, Letters, TriviaStatus} from "../../../state/state";
import {MCQuestionDefinition} from "../../../state/questions";
import DisplayText, {TextVariant} from "engage-client/components/DisplayText";

//TODO:Need to figure out how to do multiple computed properties within one class
// @ts-ignore
const useStyles = createUseStyles<Theme>((theme) => ({
	mcDisplay: {
		display:"flex",
		flexDirection: "column",
		margin: [8, 32],
		[theme.breakpoints.down("xs")]: {
			margin: 0,
		},
		[theme.breakpoints.up("lg")]: {
			flexDirection: "row",
			flexWrap: "wrap"
		},
		[theme.breakpoints.up("xxl")]: {
			margin: [20, 80]
		}
	},
	optionContainer: {
		marginBottom: 24,
		padding: [0, 16],
		boxSizing: "border-box",
		[theme.breakpoints.up("lg")]: {
			width: "50%"
		},
		[theme.breakpoints.up("xxl")]: {
			padding: [0, "1vw"],
			marginBottom: "1vw"
		}
	},
	totalNumber: {
		width: "100%",
		textAlign: "center"
	}
}));

type MultipleChoiceDisplayProps = QuestionDisplayProps & {
	answerDistribution?: number[]
}

export function getAnswerDistribution(state:TriviaStatus, aggregateResponses:AggregateResponses|undefined, mcQuestion:MCQuestionDefinition) {
	let answerDistribution:number[]|undefined = undefined;

	if(aggregateResponses) {
		const optionCounts = aggregateResponses.optionCounts;
		answerDistribution = new Array(mcQuestion.options.length).fill(0);
		for(let i = 0; i < mcQuestion.options.length; i++) {
			answerDistribution[i] = optionCounts[i] ?? 0;
		}
	}
	return answerDistribution;
}

export function getAnswerPercentages(state: TriviaStatus, answerDistribution:number[]|undefined, mcQuestion:MCQuestionDefinition) {
	const percentages = new Array(mcQuestion.options.length).fill(100);

	if(answerDistribution) {
		const total = answerDistribution.reduce((acc, val) => acc + val, 0);

		for(let i = 0; i < percentages.length; i++) {
			if(total > 0) {
				percentages[i] = (answerDistribution[i] / total) * 100;
			} else {
				percentages[i] = 0;
			}
		}
	}
	return percentages;
	
}

export default function MultipleChoiceDisplay({state, question, aggregateResponses} : MultipleChoiceDisplayProps) {
	const classes = useStyles();
	const theme = useTheme<Theme>();
	//Guaranteed to be an MCQuestionDefinition based on Question.tsx logic
	const mcQuestion = question as MCQuestionDefinition;
	
	let answerDistribution = getAnswerDistribution(state, aggregateResponses, mcQuestion);
	let percentages = getAnswerPercentages(state, answerDistribution, mcQuestion);
	let total = 0;
	if(state === TriviaStatus.QuestionSubmit && answerDistribution) {
		total = answerDistribution.reduce((acc, val) => acc + val, 0);
	}
	
	return <div className={classes.mcDisplay}>
		{mcQuestion.options.map((option, index) => {
			const colors = MCColors(theme);
			let color = colors[index % colors.length];
			let selected = false;
			if (state === TriviaStatus.QuestionAnswer && !question.poll) {
				if(index !== mcQuestion.correctIndex) {
					color = MCDisabledColor(theme);
				} else {
					selected = true;
				}
			}
			if(answerDistribution) {
				selected = true;
			}
			return <div key={index} className={classes.optionContainer} data-qa="mc-display" data-value={index}>
				<MultipleChoiceOption
					index={index}
					color={color}
					percent={percentages[index]}
					answerCount={answerDistribution ? answerDistribution[index] : undefined}
					label={`${Letters[index]}`}
					selected={selected}>{option}</MultipleChoiceOption>
			</div>;
		})
		}
		{total > 0 &&
			<DisplayText className={classes.totalNumber} variant={TextVariant.s16reg} color="white">Answers Received: {total}</DisplayText>
		}
	</div>;
}